/**
 * This component is responsible for rendering specific match task groups
 *
 * @todo Adjust this with training and other categories too and then rename this component too.
 */

import React, { useEffect, useState } from 'react'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'
import { Translate } from 'react-localize-redux'
import { fetchTaskGroups, deleteTaskGroup } from 'api/taskGroups/api'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import AlertDialog from 'components/AlertDialog'
import SubLink from 'containers/SubLink'
import { LoadingSpinner } from 'components'
import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import { PlayersLoader } from 'loaders'
import PropTypes from 'prop-types'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import AddTaskGroupDialog from './AddTaskGroupDialog'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'
import { TRAININGS_GAME } from 'constants/calender'

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    overflow: 'auto',
    height: 204,
    padding: theme.spacing(0),
    '& .MuiListItemSecondaryAction-root': {
      right: 12,
    },
    '& .MuiListItemIcon-root': {
      minWidth: 15,
    },
    '& .MuiAvatarGroup-avatar': {
      width: theme.spacing(3),
      height: theme.spacing(3),
      fontSize: 10,
    },
  },
  colorBadge: {
    width: 5,
    height: 5,
    borderRadius: 50,
    backgroundColor: theme.palette.primary.main,
  },
  listItemText: {
    maxWidth: '50%',
  },
  listItemTextEllipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}))

const MatchTaskGroupsWidget = ({ players, teamUuid, eventId }) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [taskGroup, setTaskGroup] = useState(null)
  const [openAlert, setOpenAlert] = useState(false)
  const [deleteTaskId, setDeleteTaskId] = useState(false)
  const [teamTaskGroups, setTeamTaskGroups] = useState([])
  const [taskGroupsState, setTaskGroupsState] = useState([])
  const [isLoading, setLoading] = useState(false)
  const featureGuard = useFeatureAccessGuard()

  const loadTaskGroups = async () => {
    setLoading(true)
    try {
      const res = await fetchTaskGroups(teamUuid, {
        'filter[event_type]': TRAININGS_GAME.match,
        'filter[event_uuid]': eventId,
      })
      setTeamTaskGroups(res.data)
    } catch (error) {
      console.error('Error fetching task groups:', error)
    } finally {
      setLoading(false)
    }
  }

  const { enabled: hasTaskManagerEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.task_manager_enabled
  )

  const handleOpen = (selectedTaskGroup) => {
    if (featureGuard(hasTaskManagerEnabled)) return

    setTaskGroup(selectedTaskGroup)
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
    setOpenAlert(false)
  }
  const handleAlertOpen = () => {
    setOpenAlert(true)
  }
  const openAlertBox = (taskId) => {
    if (featureGuard(hasTaskManagerEnabled)) return

    if (taskId) {
      setDeleteTaskId(taskId)
      handleAlertOpen()
    }
  }
  const handleDelete = async () => {
    try {
      await deleteTaskGroup(deleteTaskId)
      await loadTaskGroups()
    } catch (error) {
      console.error('Error deleting task group:', error)
    } finally {
      setOpenAlert(false)
    }
  }

  useEffect(() => {
    if (!eventId) return

    loadTaskGroups()
  }, [teamUuid, eventId])

  useEffect(() => {
    if (players) {
      setTaskGroupsState(
        teamTaskGroups.map((t) => {
          return {
            ...t,
            assignees: t.person_uuids
              .map((personUuid) =>
                players.find((player) => player.uuid === personUuid)
              )
              .filter((m) => m !== undefined),
          }
        })
      )
    }
  }, [teamTaskGroups, players])

  return (
    <>
      {isLoading && <LoadingSpinner />}
      <PlayersLoader>
        {/* We need to load players so that we can attach them in task assignees
          and in add task group dialog to select task for players */}
        <Card>
          {open && (
            <AddTaskGroupDialog
              open={open}
              onClose={handleClose}
              onSuccess={loadTaskGroups}
              selectedTaskGroup={taskGroup}
              eventId={eventId}
              isMatchEvent
            />
          )}
          <CardHeader
            title={
              <Typography variant="h4" component="h4">
                <Translate id="task-management.title" />
              </Typography>
            }
            action={
              <>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleOpen(null)}
                  endIcon={
                    !hasTaskManagerEnabled ? (
                      <LockIconComponent context="primaryButton" />
                    ) : null
                  }
                >
                  <Translate id="task-management.add-task" />
                </Button>
              </>
            }
          />
          <CardContent className={classes.root}>
            <List>
              {isLoading && <LoadingSpinner />}
              {!isLoading &&
                taskGroupsState.map((element) => (
                  <ListItem
                    key={element.uuid}
                    dense="true"
                    disablePadding="true"
                  >
                    <ListItemIcon>
                      <Box component="span" className={classes.colorBadge} />
                    </ListItemIcon>
                    <ListItemText
                      className={classes.listItemText}
                      primary={
                        <Tooltip title={element.title} placement="top" arrow>
                          <Box className={classes.listItemTextEllipsis}>
                            <SubLink to={`/task-group/${element.uuid}`}>
                              {element.title}
                            </SubLink>
                          </Box>
                        </Tooltip>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-start"
                      >
                        <Box>
                          <Tooltip title="Assignee" placement="top" arrow>
                            <AvatarGroup>
                              {element?.assignees?.slice(0, 2).map((player) => (
                                <Avatar
                                  key={player?.id}
                                  alt={player?.first_name}
                                  src={player?.avatar?.full_url}
                                />
                              ))}
                            </AvatarGroup>
                          </Tooltip>
                        </Box>
                        <Box>
                          {element?.assignees.length > 2
                            ? `+${element?.assignees.length - 2}`
                            : ''}
                        </Box>
                        <Box>
                          <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={() => handleOpen(element)}
                            disabled={element.completed}
                          >
                            <Tooltip
                              title={<Translate id="button.edit" />}
                              placement="top"
                              arrow
                            >
                              <EditIcon fontSize="small" />
                            </Tooltip>
                          </IconButton>
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => openAlertBox(element.uuid)}
                          >
                            <Tooltip
                              title={<Translate id="button.delete" />}
                              placement="top"
                              arrow
                            >
                              <DeleteIcon fontSize="small" />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
            </List>
          </CardContent>
          {openAlert && (
            <AlertDialog
              open={openAlert}
              onClose={handleClose}
              onSuccess={handleDelete}
              onCancel={handleClose}
              title={<Translate id="button.delete" />}
              subTitle={
                <Translate id="events.are-you-sure-you-want-to-delete" />
              }
              submitButtonLabel={<Translate id="button.ok" />}
              cancelButtonLabel={<Translate id="button.close" />}
            />
          )}
        </Card>
      </PlayersLoader>
    </>
  )
}

MatchTaskGroupsWidget.defaultProps = {
  eventId: 0,
}

MatchTaskGroupsWidget.propTypes = {
  players: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  teamUuid: PropTypes.string.isRequired,
  eventId: PropTypes.number,
}
export default compose(
  withRouter,
  connect(({ players, team }) => ({
    players: players.items,
    teamUuid: team.uuid,
  }))
)(MatchTaskGroupsWidget)
